/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";

// @mui icons
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import { useState, useEffect, useRef } from "react";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ProfileInfoCard from "examples/Cards/InfoCards/ProfileInfoCard";

import ProfilesList from "layouts/pages/profile/profile-overview/components/ProfilesList";
import MDButton from "components/MDButton";
import { Link } from "react-router-dom";

// Overview page components
import Header from "layouts/pages/profile/components/Header";
import PlatformSettings from "layouts/pages/profile/profile-overview/components/PlatformSettings";

import axios from "axios";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";
import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";

function Overview() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [dinasId, setDinasId] = useState(null);
  const [uid_RSS, setUid_RSS] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [tugas, setTugas] = useState([]);
  const [disposisi, setDisposisi] = useState([]);
  const [overviewItems, setOverviewItems] = useState([]);
  const [openEditAvatarDialog, setOpenEditAvatarDialog] = useState(false);
  const [editNoHP, setEditNoHP] = useState("");
  const [editFacebook, setEditFacebook] = useState("");
  const [editInstagram, setEditInstagram] = useState("");
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isValidFile, setIsValidFile] = useState(false);
  const fileInputRef = useRef(null);
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setUserId(user.uid);
      setDinasId(user.dinas_id);
      setUid_RSS(user.uid_RSS);
    } else {
      logoutUser(navigate);
    }
  }, []);

  // Fungsi untuk mengubah teks menjadi proper case
  const toProperCase = (text) => {
    if (!text) return "";
    return text
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  useEffect(() => {
    if (userId) {
      fetchUserDetail();
      fetchTugas();
      fetchDisposisi();
    }
  }, [userId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    // Gabungkan instruksi dan disposisi, urutkan terbaru, ambil max 5
    const instruksiItems = (tugas || []).map((t) => ({
      type: "instruksi",
      id: t.instruksi_id,
      date: t.createdAt || t.updatedAt,
      title: t.tugas?.judul,
      description: t.tugas?.deskripsi,
      prioritas: t.tugas?.prioritas,
      status: t.tugas?.status,
      penerima: t.penerima_instruksi_yang_sama,
      pemberi: t.pemberi || { nama: "-", profil: null },
      data: t,
    }));
    const disposisiItems = (disposisi || []).map((d) => ({
      type: "disposisi",
      id: d.disposisi_id,
      date: d.createdAt || d.tanggal_disposisi,
      title: d.perihal,
      description: d.catatan_disposisi,
      nomor_surat: d.nomor_surat,
      status: d.penerima?.status,
      pemberi: {
        nama: d.nama_pengirim || "-",
        profil: d.profil_pengirim
          ? d.profil_pengirim.startsWith("http")
            ? d.profil_pengirim
            : `https://esa-office.rotendaokab.go.id${d.profil_pengirim}`
          : null,
      },
      data: d,
    }));
    const merged = [...instruksiItems, ...disposisiItems].sort(
      (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
    );
    setOverviewItems(merged.slice(0, 5));
  }, [tugas, disposisi]);

  const fetchTugas = async () => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}users/tugas/${userId}`
      );
      setTugas(response.data || []);
    } catch (error) {
      console.error("Error fetching tugas:", error);
      setTugas([]);
    }
  };

  const fetchDisposisi = async () => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}disposisi/disposisi-penerima/${userId}?filter=all&limit=5`
      );
      setDisposisi(response.data || []);
    } catch (error) {
      console.error("Error fetching disposisi:", error);
      setDisposisi([]);
    }
  };

  const fetchUserDetail = async () => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}users/detail/${userId}`
      );
      setUserDetail(response.data);
      console.log("profil list ", response.data);
    } catch (error) {
      console.error("Error fetching user detail:", error);
    }
  };

  const handleOpenEditAvatarDialog = () => {
    setOpenEditAvatarDialog(true);
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsValidFile(false);
    setEditNoHP(userDetail?.noHP || userDetail?.no_hp || "");
    setEditFacebook(userDetail?.facebook || "");
    setEditInstagram(userDetail?.instagram || "");
  };

  const handleCloseEditAvatarDialog = () => {
    setOpenEditAvatarDialog(false);
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setIsValidFile(false);
    setUploading(false);
    setEditNoHP("");
    setEditFacebook("");
    setEditInstagram("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];

    // Cleanup preview URL sebelumnya jika ada
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    if (!file) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsValidFile(false);
      return;
    }

    // Validasi ukuran file (maksimal 1MB)
    if (file.size > 1 * 1024 * 1024) {
      swal({
        title: "Error",
        text: "Ukuran file melebihi 1 MB. Silakan pilih file yang lebih kecil",
        icon: "error",
      });
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsValidFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Validasi tipe file
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      swal({
        title: "Error",
        text: "Format file tidak didukung. Hanya JPG dan PNG yang diperbolehkan",
        icon: "error",
      });
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsValidFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // File valid
    setSelectedFile(file);
    setIsValidFile(true);

    // Buat preview URL menggunakan createObjectURL untuk efisiensi
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
  };

  const handleEditIconClick = () => {
    fileInputRef.current?.click();
  };

  const hasProfileChanges = () => {
    const currentNoHP = userDetail?.noHP || userDetail?.no_hp || "";
    const currentFacebook = userDetail?.facebook || "";
    const currentInstagram = userDetail?.instagram || "";
    return (
      editNoHP !== currentNoHP ||
      editFacebook !== currentFacebook ||
      editInstagram !== currentInstagram
    );
  };

  const handleSaveProfile = async () => {
    const hasFile = !!selectedFile && isValidFile;
    const hasProfile = hasProfileChanges();

    if (!hasFile && !hasProfile) {
      swal({
        title: "Info",
        text: "Tidak ada perubahan untuk disimpan",
        icon: "info",
      });
      return;
    }

    setUploading(true);
    try {
      let successMessages = [];

      // 1. Upload avatar jika ada file
      if (hasFile && uid_RSS) {
        const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
        const validExtension = ["jpg", "jpeg", "png"].includes(fileExtension)
          ? fileExtension === "jpeg"
            ? "jpg"
            : fileExtension
          : "jpg";
        const renamedFile = new File(
          [selectedFile],
          `${uid_RSS}.${validExtension}`,
          { type: selectedFile.type, lastModified: selectedFile.lastModified }
        );
        const formData = new FormData();
        formData.append("photo", renamedFile);
        const uploadResponse = await axios.post(
          `https://smartdh.rotendaokab.go.id/api/users/upload-avatar`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (uploadResponse.data?.success === 1) {
          successMessages.push("Gambar profil");
          if (previewUrl) URL.revokeObjectURL(previewUrl);
        } else {
          throw new Error(uploadResponse.data?.message || "Gagal mengupload gambar");
        }
      }

      // 2. Update profil (noHP, Facebook, Instagram) jika ada perubahan
      if (hasProfile && userId) {
        await axios.patch(
          `${API_ESA_OFFICE}users/${userId}/profile`,
          {
            noHP: editNoHP || null,
            facebook: editFacebook || null,
            instagram: editInstagram || null,
          }
        );
        successMessages.push("Data profil (No HP, Facebook, Instagram)");
      }

      if (successMessages.length > 0) {
        swal({
          title: "Berhasil!",
          text: `${successMessages.join(" dan ")} berhasil diupdate`,
          icon: "success",
          button: "OK",
        });
        await fetchUserDetail();
        handleCloseEditAvatarDialog();
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      swal({
        title: "Error!",
        text: error.response?.data?.message || error.message || "Gagal menyimpan profil",
        icon: "error",
        button: "OK",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <Header
        userNama={userDetail && userDetail.nama}
        userJabatan={userDetail && userDetail.nama_jabatan}
        profile={
          userDetail &&
          `https://smartdh.rotendaokab.go.id/uploads/${userDetail.uid_RSS}.jpg`
        }
        onAvatarClick={handleOpenEditAvatarDialog}
      >
        <MDBox mt={5} mb={3}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6} xl={4}>
              <PlatformSettings />
            </Grid>
            <Grid item xs={12} md={6} xl={4} sx={{ display: "flex" }}>
              <Divider orientation="vertical" sx={{ ml: -2, mr: 1 }} />
              <ProfileInfoCard
                title="profile information"
                description="Seorang Aparatur Sipil Negara yang berdedikasi tinggi, selalu berkomitmen untuk memberikan pelayanan terbaik kepada masyarakat dan berkontribusi positif dalam pembangunan bangsa."
                info={{
                  fullName: userDetail && toProperCase(userDetail.nama),
                  mobile: userDetail && (userDetail.noHP || userDetail.no_hp),
                  email: userDetail && userDetail.email,
                  Dinas: userDetail && toProperCase(userDetail.nama_dinas),
                }}
                social={[
                  {
                    link: `https://www.facebook.com/${
                      userDetail && userDetail.facebook
                    }`,
                    icon: <FacebookIcon />,
                    color: "facebook",
                  },
                  {
                    link: `https://www.instagram.com/${
                      userDetail && userDetail.instagram
                    }`,
                    icon: <InstagramIcon />,
                    color: "instagram",
                  },
                ]}
                action={{ route: "", tooltip: "Edit Profile" }}
                shadow={false}
              />
              <Divider orientation="vertical" sx={{ mx: 0 }} />
            </Grid>
            <Grid item xs={12} xl={4}>
              <ProfilesList
                title={userDetail && userDetail.nama_bidang}
                profiles={userDetail ? userDetail.teman_bidang : []}
                shadow={false}
              />
            </Grid>
          </Grid>
        </MDBox>
        <MDBox pt={2} px={2} lineHeight={1.25}>
          <MDTypography variant="h6" fontWeight="medium">
            Overview Tugas & Disposisi
          </MDTypography>
          <MDBox mb={1}>
            <MDTypography variant="button" color="text">
              5 Instruksi dan Disposisi terbaru
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox p={2}>
          <Grid container spacing={3}>
            {overviewItems.length === 0 ? (
              <Grid item xs={12}>
                <Card sx={{ overflow: "visible" }}>
                  <MDBox p={4} textAlign="center">
                    <Icon sx={{ fontSize: 48, opacity: 0.3 }}>inbox</Icon>
                    <MDTypography variant="body2" color="text" mt={1}>
                      Belum ada tugas atau disposisi
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
            ) : (
              overviewItems.map((item) => {
                const isInstruksi = item.type === "instruksi";
                const accentColor = isInstruksi ? "info" : "success";
                const detailRoute = isInstruksi
                  ? `/task-management/tugas/instruksi-detail/${item.id}`
                  : `/task-management/disposisi/disposisi-detail/${item.id}`;
                const pemberiProfil = item.pemberi?.profil || null;

                return (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    xl={4}
                    key={`${item.type}-${item.id}`}
                  >
                    <Card
                      sx={{
                        overflow: "visible",
                        boxShadow: ({ boxShadows: { md } }) => md,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: ({ boxShadows: { lg } }) => lg,
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <MDBox
                        display="flex"
                        alignItems="flex-start"
                        p={2}
                        sx={{
                          borderLeft: "4px solid",
                          borderColor: `${accentColor}.main`,
                        }}
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          width={48}
                          height={48}
                          borderRadius="lg"
                          bgColor={`${accentColor}.main`}
                          color="white"
                          flexShrink={0}
                          mr={2}
                        >
                          {isInstruksi ? (
                            <AssignmentIcon fontSize="medium" />
                          ) : (
                            <DescriptionIcon fontSize="medium" />
                          )}
                        </MDBox>
                        <MDBox flex={1} minWidth={0}>
                          <MDBox
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={0.5}
                          >
                            <MDTypography
                              variant="caption"
                              fontWeight="bold"
                              color={accentColor}
                              textTransform="uppercase"
                            >
                              {isInstruksi ? "Instruksi" : "Disposisi"}
                            </MDTypography>
                            {isInstruksi && item.prioritas && (
                              <MDTypography
                                variant="caption"
                                color="text"
                                sx={{
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  bgcolor: "grey.200",
                                }}
                              >
                                Prioritas: {item.prioritas}
                              </MDTypography>
                            )}
                            {!isInstruksi && item.nomor_surat && (
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                • {item.nomor_surat}
                              </MDTypography>
                            )}
                          </MDBox>
                          <MDTypography
                            variant="h6"
                            fontWeight="medium"
                            color="dark"
                            sx={{
                              lineHeight: 1.3,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {item.title || (isInstruksi ? "Tanpa judul" : "Tanpa perihal")}
                          </MDTypography>
                          <MDBox
                            mt={1}
                            sx={{
                              maxHeight: 60,
                              overflow: "hidden",
                              "& p": { margin: 0, fontSize: "0.8rem" },
                            }}
                          >
                            {isInstruksi ? (
                              <MDTypography
                                variant="body2"
                                color="text"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                                dangerouslySetInnerHTML={{
                                  __html:
                                    (item.description || "").replace(
                                      /<[^>]*>/g,
                                      " "
                                    ) || "-",
                                }}
                              />
                            ) : (
                              <MDTypography
                                variant="body2"
                                color="text"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {item.description || "-"}
                              </MDTypography>
                            )}
                          </MDBox>
                          <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mt={2}
                            flexWrap="wrap"
                            gap={1}
                          >
                            <MDBox display="flex" alignItems="center" gap={1}>
                              <PersonIcon
                                sx={{ fontSize: 16, color: "text.secondary" }}
                              />
                              <MDAvatar
                                src={pemberiProfil}
                                alt={item.pemberi?.nama}
                                size="xs"
                                bgColor={accentColor}
                                sx={{ width: 24, height: 24, fontSize: "0.75rem" }}
                              >
                                {!pemberiProfil && item.pemberi?.nama?.[0]?.toUpperCase()}
                              </MDAvatar>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                fontWeight="medium"
                              >
                                Dari: {item.pemberi?.nama || "-"}
                              </MDTypography>
                            </MDBox>
                            <MDButton
                              component={Link}
                              to={detailRoute}
                              variant="outlined"
                              size="small"
                              color={accentColor}
                            >
                              Lihat detail
                            </MDButton>
                          </MDBox>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mt={0.5}
                          >
                            {formatDate(item.date)}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>
        </MDBox>
      </Header>

      {/* Dialog Edit Profil (Avatar + No HP, Facebook, Instagram) */}
      <Dialog
        open={openEditAvatarDialog}
        onClose={handleCloseEditAvatarDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Edit Profil</MDTypography>
            <IconButton onClick={handleCloseEditAvatarDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              mb={3}
            >
              <MDBox position="relative" display="inline-block">
                <MDAvatar
                  src={
                    previewUrl ||
                    (userDetail &&
                      `https://smartdh.rotendaokab.go.id/uploads/${userDetail.uid_RSS}.jpg`)
                  }
                  alt="profile-preview"
                  size="xxl"
                  shadow="md"
                  sx={{ width: 150, height: 150 }}
                />
                <IconButton
                  onClick={handleEditIconClick}
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    backgroundColor: "info.main",
                    color: "white",
                    "&:hover": { backgroundColor: "info.dark" },
                    width: 40,
                    height: 40,
                  }}
                >
                  <EditIcon />
                </IconButton>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />
              </MDBox>
              <MDBox mt={2} textAlign="center">
                <MDTypography variant="button" fontWeight="regular" color="text" mb={1}>
                  Pilih Gambar Profil
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary" display="block">
                  Format: JPG, PNG | Maksimal: 1 MB
                </MDTypography>
              </MDBox>
              {selectedFile && (
                <MDBox mt={2} textAlign="center">
                  <MDTypography variant="caption" color="text.secondary">
                    File: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
            <Divider sx={{ my: 2 }} />
            <MDBox>
              <MDTypography variant="button" fontWeight="medium" color="text" mb={2} display="block">
                Informasi Kontak
              </MDTypography>
              <TextField
                fullWidth
                label="No. HP"
                value={editNoHP}
                onChange={(e) => setEditNoHP(e.target.value)}
                margin="normal"
                size="small"
                placeholder="08xxxxxxxxxx"
              />
              <TextField
                fullWidth
                label="Facebook"
                value={editFacebook}
                onChange={(e) => setEditFacebook(e.target.value)}
                margin="normal"
                size="small"
                placeholder="username atau URL"
              />
              <TextField
                fullWidth
                label="Instagram"
                value={editInstagram}
                onChange={(e) => setEditInstagram(e.target.value)}
                margin="normal"
                size="small"
                placeholder="username"
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditAvatarDialog} disabled={uploading}>
            Batal
          </Button>
          <Button
            onClick={handleSaveProfile}
            variant="contained"
            color="info"
            disabled={uploading || (!isValidFile && !hasProfileChanges())}
            startIcon={
              uploading ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {uploading ? "Menyimpan..." : "Simpan"}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Overview;
